import { useState } from "react"
import { router } from "@inertiajs/react"
import { LoaderCircle, TriangleAlert } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatDateTime } from "@/lib/formatters"
import { formatMoney } from "./money"
import { t } from "@/lib/i18n"
import type { SelfInvoicePrompt } from "./types"

type SelfInvoiceFormProps = {
  invoice: SelfInvoicePrompt
  errors: string[]
}

/**
 * The four fields of a CFDI 4.0 *receptor*, plus what the diner will do with the
 * document.
 *
 * All five are the SAT's requirements, not Menny's, which is why none of them is
 * optional and why the copy names the *constancia de situación fiscal* — the one
 * piece of paper every one of these values is printed on. A diner who is told
 * "régimen fiscal" with no hint of where to find it abandons the form.
 *
 * An explicit submit button rather than inline commit: this is a create, and it
 * produces a legal document that costs the restaurant a stamp. `.claude/rules/
 * system-design.md` draws that line — inline is for a price or a status, not for
 * a transaction somebody files with the tax authority.
 */
export function SelfInvoiceForm({ invoice, errors }: SelfInvoiceFormProps) {
  const [rfc, setRfc] = useState("")
  const [legalName, setLegalName] = useState("")
  const [postalCode, setPostalCode] = useState("")
  const [taxRegimeCode, setTaxRegimeCode] = useState("")
  const [usageCode, setUsageCode] = useState(invoice.defaultUsageCode)
  const [submitting, setSubmitting] = useState(false)

  const complete =
    rfc.trim().length > 0 &&
    legalName.trim().length > 0 &&
    postalCode.trim().length > 0 &&
    taxRegimeCode.length > 0 &&
    usageCode.length > 0

  const submit = () => {
    if (!complete || submitting) return

    setSubmitting(true)
    router.post(
      `/invoice/${invoice.token}`,
      {
        rfc,
        legal_name: legalName,
        postal_code: postalCode,
        tax_regime_code: taxRegimeCode,
        usage_code: usageCode,
      },
      { onFinish: () => setSubmitting(false) }
    )
  }

  return (
    <form
      data-testid="self-invoice-form"
      className="w-full space-y-6 px-4 pb-10 pt-4"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      {/* What is being invoiced, before what it will say. The diner arrived from
          a receipt and may have several; naming the restaurant and the figure is
          how they know they are on the right one. */}
      <div className="rounded-2xl border bg-card p-4 text-sm" data-testid="self-invoice-summary">
        <p className="font-semibold">{invoice.locationName}</p>
        {invoice.paidAt && (
          <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
            {t("ui.react.features.user.self_invoice_form.paid_on", {
              date: formatDateTime(invoice.paidAt, { timeZone: invoice.timeZone }),
            })}
          </p>
        )}
        <p className="mt-2 flex items-baseline justify-between">
          <span className="text-muted-foreground">
            {t("ui.react.features.user.self_invoice_form.total")}
          </span>
          <span className="font-semibold tabular-nums">
            {formatMoney(invoice.total, { currency: invoice.currency })}
          </span>
        </p>
      </div>

      {errors.length > 0 && (
        <div
          data-testid="self-invoice-errors"
          className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-1">
            {errors.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="self-invoice-rfc">
            {t("ui.react.features.user.self_invoice_form.rfc")}
          </Label>
          <Input
            id="self-invoice-rfc"
            name="rfc"
            value={rfc}
            /* Upcased as it is typed, because an RFC is uppercase by definition
               and the backend normalises it anyway — seeing it match the
               constancia while typing is what catches a wrong character. */
            onChange={(event) => setRfc(event.target.value.toUpperCase())}
            maxLength={13}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            placeholder="XAXX010101000"
            data-testid="self-invoice-rfc"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="self-invoice-legal-name">
            {t("ui.react.features.user.self_invoice_form.legal_name")}
          </Label>
          <Input
            id="self-invoice-legal-name"
            name="legal_name"
            value={legalName}
            onChange={(event) => setLegalName(event.target.value)}
            autoComplete="organization"
            data-testid="self-invoice-legal-name"
          />
          <p className="text-xs text-muted-foreground">
            {t("ui.react.features.user.self_invoice_form.legal_name_hint")}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="self-invoice-postal-code">
            {t("ui.react.features.user.self_invoice_form.postal_code")}
          </Label>
          <Input
            id="self-invoice-postal-code"
            name="postal_code"
            value={postalCode}
            onChange={(event) => setPostalCode(event.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            maxLength={5}
            autoComplete="postal-code"
            data-testid="self-invoice-postal-code"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="self-invoice-tax-regime">
            {t("ui.react.features.user.self_invoice_form.tax_regime")}
          </Label>
          <Select value={taxRegimeCode} onValueChange={setTaxRegimeCode}>
            <SelectTrigger id="self-invoice-tax-regime" className="w-full" data-testid="self-invoice-tax-regime">
              <SelectValue
                placeholder={t("ui.react.features.user.self_invoice_form.tax_regime_placeholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {invoice.taxRegimes.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-testid={`self-invoice-tax-regime-option-${option.value}`}
                >
                  {option.value} — {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="self-invoice-usage">
            {t("ui.react.features.user.self_invoice_form.usage")}
          </Label>
          <Select value={usageCode} onValueChange={setUsageCode}>
            <SelectTrigger id="self-invoice-usage" className="w-full" data-testid="self-invoice-usage">
              <SelectValue
                placeholder={t("ui.react.features.user.self_invoice_form.usage_placeholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {invoice.usageCodes.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  data-testid={`self-invoice-usage-option-${option.value}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {t("ui.react.features.user.self_invoice_form.constancia_hint")}
      </p>

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={!complete || submitting}
        data-testid="self-invoice-submit"
      >
        {submitting && <LoaderCircle className="animate-spin" />}
        {t("ui.react.features.user.self_invoice_form.submit")}
      </Button>
    </form>
  )
}
